/**
 * socaity-js public API.
 * Mirrors the Python SDK's entry points:
 *
 *     import { socaity } from 'socaity';
 *
 *     socaity.setApiKey('sk_...');
 *     const client = await socaity.connect('http://localhost:8000');
 *     const job = client.submitJob('/text2voice', { text: 'hello world' });
 *     const audio = await job;              // typed media file
 *     audio.save('hello.mp3');
 *
 * Streaming:
 *
 *     const job = client.submitJob('/chat', { messages, stream: true });
 *     for await (const text of (await job.stream()).iterText()) { ... }
 */

import {
  InferenceBackendClient,
  InferenceBackendClientOptions,
} from './clients/gateway/client';
import { loadServiceDefinition } from './clients/gateway/openapi';
import { config, configure } from './core/config';
import { ServiceDefinition } from './schemas/definitions';

// ---------------------------------------------------------------------------
// Entry points
// ---------------------------------------------------------------------------

/**
 * Connect to a running Socaity/APIPod service by URL: loads its OpenAPI spec
 * and returns a ready-to-use client. The counterpart of Python's `fastsdk.connect`.
 */
export async function connect(
  url: string,
  options: InferenceBackendClientOptions = {},
): Promise<InferenceBackendClient> {
  const definition = await loadServiceDefinition(url);
  return new InferenceBackendClient(definition, { address: url, ...options });
}

/** Create a client from an already known ServiceDefinition (e.g. from the Socaity catalog). */
export function createClient(
  definition: ServiceDefinition,
  options: InferenceBackendClientOptions = {},
): InferenceBackendClient {
  return new InferenceBackendClient(definition, options);
}

/** Global SDK facade: configuration plus the client factories. */
export const socaity = {
  config,
  configure,
  connect,
  createClient,
  setApiKey(apiKey: string): void {
    configure({ apiKey });
  },
  /** Session / JWT Bearer token (frontend login). Prefer this over stuffing JWTs into apiKey. */
  setAccessToken(accessToken: string | undefined): void {
    configure({ accessToken: accessToken || undefined });
  },
  setBearerToken(token: string | undefined): void {
    configure({ accessToken: token || undefined });
  },
  setBaseUrl(baseUrl: string): void {
    configure({ baseUrl });
  },
  setWebBackendUrl(webBackendUrl: string): void {
    configure({ webBackendUrl });
  },
};

// ---------------------------------------------------------------------------
// Public types & building blocks
// ---------------------------------------------------------------------------

export { InferenceBackendClient, SocaityClient } from './clients/gateway/client';
export type {
  InferenceBackendClientOptions,
  SocaityClientOptions,
} from './clients/gateway/client';
export type { PriceEstimate } from './schemas/platform/pricing';
export { SimilarService } from './schemas/platform/pricing';
export { loadServiceDefinition, parseOpenApiSpec } from './clients/gateway/openapi';
export { config, configure, ApiKeyError, resolveAuthToken } from './core/config';
export type { SocaityConfig } from './core/config';
export { ApiError, AuthError, JobError } from './core/errors';
export { Job } from './runtime/job';
export type { JobEvent } from './runtime/job';
export { StreamSession } from './streaming/session';
export { chunkText, assembleStreamBytes } from './streaming/sse';
export type { AuthRequirement } from './clients/http';
export {
  BackendClient,
  CatalogClient,
  JobsClient,
  AnalyticsClient,
  DeploymentClient,
  ProjectsClient,
  FilesClient,
} from './clients/backend';
export type {
  CatalogQuery,
  QueryEnvelope,
  JobListOptions,
  JobVisibility,
  FilesQuery,
  UploadFilesOptions,
  FileMetadataUpdate,
} from './clients/backend';
export * from './schemas';
export { ChatServiceAdapter } from './integrations';
export type { ChatServiceAdapterOptions, JobStatusSnapshot } from './integrations';

// Media handling re-exports (media-toolkit is the file layer of the SDK).
export {
  MediaFile,
  ImageFile,
  AudioFile,
  VideoFile,
  Asset3DFile,
  MediaList,
  MediaDict,
  MediaFileFactory,
  isFileReaderObject,
  isFileResult,
  isUrl,
  detectMimeTypeFromUrl,
  detectCategoryFromMimeType,
  getCategoryDisplayName,
  getAcceptedTypes,
  getMimeTypeFromExtension,
} from '@socaity/media-toolkit';

// UMD/browser global.
if (typeof window !== 'undefined') {
  (window as any).socaity = socaity;
}
